/**
 * What happens to surplus cash flow when the caller specifies nothing: it
 * accumulates as cash, and nothing else happens.
 *
 * This is the NEUTRAL behaviour, and it is the default deliberately. Sweeping
 * surplus into tax wrappers is a course of action — "put £20,000 a year into an
 * ISA and £3,600 into a pension" is a recommendation, and a projection that
 * makes it silently has embedded advice in what is meant to be a modelling aid
 * (CLAUDE.md PRODUCT). Run e12559b6 showed exactly that: contributions nobody
 * had asked for appeared in every projected year.
 *
 * A firm that wants the sweep opts into {@link SURPLUS_SWEEP_WATERFALL}, or
 * builds its own — and the engine then states the rule in words on the
 * assumptions page so the reader knows a behaviour was assumed.
 */
export const NO_SWEEP_WATERFALL = Object.freeze({
    steps: Object.freeze([Object.freeze({ target: 'cash', annualCap: null })]),
});
/**
 * The OPT-IN surplus sweep: keep a 6-months-of-spending cash buffer, then ISA
 * up to allowance, then pension up to the annual allowance, then GIA. Fully
 * re-orderable per scenario.
 *
 * Was the engine default until the fix for run e12559b6; it is now selected
 * explicitly or not at all.
 */
export const SURPLUS_SWEEP_WATERFALL = Object.freeze({
    steps: Object.freeze([
        Object.freeze({
            target: 'cash',
            annualCap: null,
            targetBalanceMonthsOfSpending: 6,
        }),
        Object.freeze({ target: 'isa', annualCap: null }),
        Object.freeze({ target: 'pension', annualCap: null }),
        Object.freeze({ target: 'gia', annualCap: null }),
    ]),
});
/**
 * @deprecated Renamed to make the choice explicit. `DEFAULT_WATERFALL` no
 * longer describes what happens by default — {@link NO_SWEEP_WATERFALL} does.
 * Kept as an alias so existing callers that deliberately want the sweep keep
 * working; new code should name {@link SURPLUS_SWEEP_WATERFALL}.
 */
export const DEFAULT_WATERFALL = SURPLUS_SWEEP_WATERFALL;
/** Default draw order: cash → GIA → ISA → pension drawdown. */
export const DEFAULT_WITHDRAWAL_WATERFALL = Object.freeze({
    steps: Object.freeze([
        Object.freeze({ target: 'cash' }),
        Object.freeze({ target: 'gia' }),
        Object.freeze({ target: 'isa' }),
        Object.freeze({ target: 'pension' }),
    ]),
});
export const DEFAULT_END_AT_CLIENT_AGE = 90;
export const MAX_PROJECTION_AGE = 110;
//# sourceMappingURL=household-types.js.map